import {useEffect, useState} from 'react'
import {useQuery} from 'react-query'
import {getTableData, getDailySoldReportCsv, getDailySoldReportCsvDownload} from '../services'
import useFilters from '../../../hook/useFilters'
import {getURLSearchParams, makeSemicolonSepratedValues} from '../../../utils/common'
import {useForm} from 'react-hook-form'
import useToastify from '../../../hook/useToastify'
import {useSelector} from 'react-redux'

export const useGetDailySoldReport = (): any => {
  const {toastMessage} = useToastify()

  const params = getURLSearchParams()
  const [isFetchingCsvData, setIsFetchingCsvData] = useState(false)
  const [isLoadingCsv, setIsLoadingCsv] = useState(false)
  const {register, watch, reset} = useForm({defaultValues: {skus: []}})
  const [selectedSkus, setSelectedSkus] = useState(watch())
  const {global_user, global_suppliers} = useSelector((state: any) => state.filters)

  useEffect(() => {
    const subscription = watch((data) => {
      const newData: any = data
      setSelectedSkus(newData)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const initialFilters = {
    page: 1,
    limit: 30,
    brand: '',
    classification: '',
    primary_supplier: global_suppliers.length ? makeSemicolonSepratedValues(global_suppliers) : '',
    sort_by: 'total_month_quantity/-1',
    user: global_user?.value !== '' ? global_user?.value : '',
    tags: '',
    hide_discontinued: true,
    show_zero_qty_products: true,
    no_sold_days: false,
    search: '',
  }

  const {filters, setSingleFilter, setMultipleFilters} = useFilters({
    ...initialFilters,
    ...(params?.['page'] &&
      params?.['page'] > 0 && {
        page: +params['page'],
      }),
  })

  const {
    data: response,
    refetch,
    isFetching,
  } = useQuery(`get-Replenishment-data`, () => {
    return getTableData(`/analytics/replenishment/daily-sold/data`, filters)
  })

  useEffect(() => {
    refetch()
  }, [filters, refetch])

  if (!response?.['data']) {
    return []
  }

  const onSearch = (searchValue: string) => {
    setMultipleFilters({
      searchValue: searchValue,
      page: 1,
    })
  }

  const onPageChange = (page_number: number) => {
    setSingleFilter('page', page_number)
  }

  const onClickSorting = (sortBy: string, sortDirection: any) => {
    const direction = sortDirection === 'asc' ? 1 : -1
    const sortFlag = sortBy + '/' + direction
    setMultipleFilters({
      sort_by: sortFlag,
      page: 1,
    })
  }

  const onApplyFilters = (data: any) => {
    setMultipleFilters({
      page: 1,
      limit: 30,
      brand: data.brand !== '' ? data.brand : '',
      classification: data.classification !== '' ? data.classification : '',
      primary_supplier: data.primary_supplier !== '' ? data.primary_supplier : '',
      user: data.user,
      tags: data.tags !== '' ? data.tags : '',
      hide_discontinued: data.hide_discontinued,
      show_zero_qty_products: data.show_zero_qty_products,
      no_sold_days: data.no_sold_days,
    })
  }

  const onResetFilters = () => {
    setMultipleFilters({
      page: 1,
      limit: 30,
      brand: '',
      classification: '',
      primary_supplier: '',
      sort_by: ' ',
      tags: '',
      hide_discontinued: true,
      show_zero_qty_products: true,
      no_sold_days: false,
    })
  }

  const handleResetClick = () => {
    reset() // This will clear the registered fields
    setSelectedSkus({skus: []})
  }

  const onDownloadCsv = async (payload: any) => {
    setIsFetchingCsvData(true)
    const data = await getDailySoldReportCsvDownload(filters)
    let res = ''
    if (data?.status === 200) {
      res = data?.data?.message
      handleResetClick()
    } else {
      toastMessage('error', data?.data?.message)
    }
    setIsFetchingCsvData(false)
    return res
  }

  const onCsvExport = async () => {
    setIsLoadingCsv(true)
    const data = await getDailySoldReportCsv(filters)
    let res = ''
    if (data?.status === 200) {
      res = data?.data?.message
      handleResetClick()
    }
    setIsLoadingCsv(false)
    return res
  }

  return {
    replenishment: response?.['data']?.['data'] || [],
    filters,
    isFetchingReplenishmentData: isFetching,
    monthly_column: response?.['data']?.['meta']?.['day_labels'] || {},
    pagination: response?.['data']?.['meta']?.['pagination'] || {},
    onSearch,
    onPageChange,
    onClickSorting,
    onApplyFilters,
    onResetFilters,
    register,
    selectedSkus,
    onDownloadCsv,
    isFetchingCsvData: isFetchingCsvData,
    onCsvExport,
    isLoadingCsv,
  }
}
